import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { StockDataMA, StockDataMASorting } from '../dto/StockDataMA';
import { MarketAnalysisTickerService } from '../services/MarketAnalysisTickerService';
import { firstValueFrom } from 'rxjs';
import { NOTIFICATIONS, NotificationsService } from '../../../../../core/service/notifications.service';
import { RecoveryTickerChartComponent } from './sub/recovery-ticker-chart/recovery-ticker-chart.component';
import { Sectors } from './sub/recovery-ticker-modal/recovery-ticker-modal.component';
import { CheckBoxSelector } from '../../../../shared/component/checkbox-selector/checkbox-selector.component';
import { cloneDeep } from 'lodash';
import { fmpUrl } from '../../../../company-performance/companies-stocks/dto/StockAuxiliary';
import { ToolTipSettings } from '../../../../shared/component/tooltip/tooltip.util';

@Component({
  selector: 'app-ma-tickers',
  templateUrl: './ma-tickers.component.html',
  styleUrls: ['./ma-tickers.component.scss'],
})
export class MaTickersComponent implements OnInit {
  @Input() recoveryMode = false;
  @ViewChild('chartMaTicker') chartMaTicker: RecoveryTickerChartComponent;
  stocks: StockDataMA[];
  baseStocksWithoutFilters: StockDataMA[];
  allSectors: Sectors[];
  checkBoxSelectors: CheckBoxSelector[] = [];
  sectorColourMap = new Map<number, ColoursForIndustries>();
  tickerDataForEdit: StockDataMA;
  show: ShowTickerModal;
  selectedSectorsFromFilter: Sectors[] = [];
  countCompanies: number;
  protected readonly StockDataMASorting = StockDataMASorting;
  modalTickersNames = ModalTickersNames;
  infoForRevenue: InfoForModal = {
    minValue: 0,
    maxValue: 9999999999999,
  };

  infoForEnterpriseValue: InfoForModal = {
    minValue: 0,
    maxValue: 9999999999999,
  };

  infoForRevenueGrowth: InfoForModal = {
    minValue: -100,
    maxValue: 100,
  };

  toolTipSettings: ToolTipSettings = {
    show: false,
    title: '',
  };

  constructor(
    private analysisTickerService: MarketAnalysisTickerService,
    private notificationsService: NotificationsService,
  ) {}


  async ngOnInit(): Promise<void> {
    await this.initData();
  }

  async deleteTickerFroMA(tickerName: string): Promise<void> {
    await firstValueFrom(this.analysisTickerService.deleteTickerFromMA(tickerName));
    this.checkBoxSelectors = [];
    this.selectedSectorsFromFilter = [];
    await this.initData();
    await this.chartMaTicker.renderChartTicker();
    this.notificationsService.success(NOTIFICATIONS.DELETE_SUCCESS);
  }

  async closeModal(isSuccessful: boolean): Promise<void> {
    this.show.tickerModal = false;
    if (isSuccessful) {
      this.checkBoxSelectors = [];
      this.selectedSectorsFromFilter = [];
      await this.initData();
      await this.chartMaTicker.renderChartTicker();
    }
  }

  showTickerModal(tickerData?: StockDataMA): void {
    this.show.tickerModal = true;
    this.tickerDataForEdit = tickerData;
  }

  goToTickerWebsite(tickerName: string): void {
    if (!tickerName) {
      return;
    }
    window.open(fmpUrl + tickerName);
  }

  activateTooltip(i: number, sectors: Sectors[]): void {
    this.toolTipSettings.show = true;
    this.toolTipSettings.title = sectors.slice(1).map((sector) => sector.sector)
      .join(', ');
    this.toolTipSettings.coordinates = document.getElementById('sectors' + i)?.getBoundingClientRect();
  }

  hideTooltip(): void {
    if (this.toolTipSettings) {
      this.toolTipSettings.show = false;
    }
  }

  async recreateFromIndustryFilter(selectedIndustries: CheckBoxSelector[]): Promise<void> {
    this.selectedSectorsFromFilter = [];
    selectedIndustries.forEach((sectorCheckBox) => {
      if (sectorCheckBox.chosen) {
        const sector: Sectors = {
          id: sectorCheckBox.itemId,
          sector: sectorCheckBox.item,
        };
        this.selectedSectorsFromFilter.push(sector);
      }
    });
    await this.fetchFilteredData();
  }

  async recreateFromModal(infoForModal: InfoForModal, modalName: ModalTickersNames): Promise<void> {
    if (modalName === ModalTickersNames.Revenue) {
      this.infoForRevenue = {
        minValue: infoForModal.minValue,
        maxValue: infoForModal.maxValue,
      };
    } else if (modalName === ModalTickersNames.EnterpriseValue) {
      this.infoForEnterpriseValue = {
        minValue: infoForModal.minValue,
        maxValue: infoForModal.maxValue,
      };
    } else {
      this.infoForRevenueGrowth = {
        minValue: infoForModal.minValue,
        maxValue: infoForModal.maxValue,
      };
    }
    await this.fetchFilteredData();
  }

  private async fetchFilteredData(): Promise<void> {
    this.stocks = this.baseStocksWithoutFilters.filter((stock) => {

      const isSectorMatch = stock.sectors?.some((sector) => this.selectedSectorsFromFilter.map((s) => s.id).includes(sector.id)) ?? false;

      const isRevenueMatch = stock.annualRevenue !== undefined &&
        stock.annualRevenue >= this.infoForRevenue.minValue &&
        stock.annualRevenue <= this.infoForRevenue.maxValue;

      const isEnterpriseValueMatch = stock.enterpriseValue !== undefined &&
        stock.enterpriseValue >= this.infoForEnterpriseValue.minValue &&
        stock.enterpriseValue <= this.infoForEnterpriseValue.maxValue;

      const isRevenueGrowthMatch = stock.revenueGrowth !== undefined &&
        stock.revenueGrowth * 100 >= this.infoForRevenueGrowth.minValue &&
        stock.revenueGrowth * 100 <= this.infoForRevenueGrowth.maxValue;

      return isSectorMatch && isRevenueMatch && isEnterpriseValueMatch && isRevenueGrowthMatch;
    });
    this.countCompanies = this.stocks.length;
    const stocksNames = this.stocks.map((stock) => stock.tickerName);
    await this.chartMaTicker.renderChartTickerFiltered(stocksNames);
  }

  private async initData(): Promise<void> {
    if (!this.allSectors) {
      this.allSectors = await firstValueFrom(this.analysisTickerService.getAllFMSSectors());
      let colourIndex = 0;
      this.allSectors.forEach((sector) => {
        if (colourIndex >= COLOURS_FOR_INDUSTRIES.length) {
          colourIndex = 0; // Reset index if it exceeds the number of available colours
        }
        this.sectorColourMap.set(sector.id, COLOURS_FOR_INDUSTRIES[colourIndex++]);
      });
    }
    this.show = new ShowTickerModal();
    await this.fetchStocks();
    if (this.checkBoxSelectors.length > 0) {
      return;
    }
    this.stocks.forEach((stock) => {
      stock.sectors?.forEach((sector) => {
        if (!this.checkBoxSelectors.some((existed) => existed.itemId === sector.id)) {
          const checkBox: CheckBoxSelector = {
            itemId: sector.id,
            item: sector.sector,
            chosen: true,
            isHidden: false,
            disabled: false,
          };
          this.checkBoxSelectors.push(checkBox);
          this.selectedSectorsFromFilter.push(sector);
        }
      });
    });
    this.checkBoxSelectors.sort((a, b) => a.item.localeCompare(b.item));
  }

  private async fetchStocks(): Promise<void> {
    this.stocks = await firstValueFrom(this.analysisTickerService.getMAStocksByCompany());
    this.countCompanies = this.stocks.length;
    this.baseStocksWithoutFilters = cloneDeep(this.stocks);
    this.show.emptyState = !this.stocks.length;
  }
}
class ShowTickerModal {
  tickerModal = false;
  emptyState = false;
}
export class InfoForModal {
  minValue: number;
  maxValue: number;
}

export enum ModalTickersNames {
  EnterpriseValue = 'Enterprise value',
  Revenue = 'Revenue',
  RevenueGrowthYOY = 'Rev. Growth YOY',
  Industries = 'Industries'
}

export const COLOURS_FOR_INDUSTRIES = [
  '#005B96', '#DAA520', '#008080', '#A52A2A', '#9ACD32', '#4B0082',
  '#FF4500', '#8B0000', '#483D8B', '#00008B', '#8B4513', '#BC8F8F',
  '#4682B4', '#191970', '#696969', '#808000', '#008B8B', '#A0522D',
  '#556B2F', '#D2691E',
] as const;

export type ColoursForIndustries = typeof COLOURS_FOR_INDUSTRIES[number];
